import React from 'react';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { getNestedValue } from '../utils/dataBinding';

interface FaqItemComponentProps {
    component: ComponentNode;
    data?: any;
}

const resolveValue = (node: ComponentNode, itemData: any) => {
    if (!node.placeholder) {
        return node.data;
    }

    return getNestedValue(itemData, node.placeholder) ?? node.data;
};

const hasHtmlContent = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const renderFaqChild = (node: ComponentNode, itemData: any) => {
    const inlineStyles = convertStylesToInline(node.styles);
    const value = resolveValue(node, itemData);
    const textValue = typeof value === 'string' ? value : '';

    switch (node.type) {
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4': {
            const HeadingTag = node.type.toLowerCase() as 'h1' | 'h2' | 'h3' | 'h4';
            return React.createElement(HeadingTag, { style: inlineStyles }, value);
        }

        case 'P':
        case 'TEXT':
            if (textValue && hasHtmlContent(textValue)) {
                return <div style={inlineStyles} dangerouslySetInnerHTML={{ __html: textValue }} />;
            }

            return <p style={inlineStyles}>{value}</p>;

        default:
            return null;
    }
};

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 18px',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    listStyle: 'none',
};

/**
 * `accordion` (default): native <details>/<summary> disclosure, first item
 * open. `list`: every question/answer always visible, no click needed.
 * `grid`: same accordion behavior, 2 columns instead of one long list.
 * Mirrors renderNode.tsx's FAQ_ITEM case exactly — see that file for why
 * this reads `component.layoutVariant` directly rather than the generic
 * gridCols mechanism other collection blocks use.
 */
const FaqItemComponent: React.FC<FaqItemComponentProps> = ({ component, data }) => {
    const { styles, components, repeated, totalCards, layoutVariant } = component as ComponentNode & { layoutVariant?: string };
    const inlineStyles = convertStylesToInline(styles);
    const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
            ? data.data
            : [];
    const repeatedItems = repeated ? (totalCards ? items.slice(0, totalCards) : items) : [];
    const questionNode = components?.[0];
    const answerNodes = components?.slice(1) || [];
    const variant = layoutVariant === 'list' || layoutVariant === 'grid' ? layoutVariant : 'accordion';

    const renderItem = (item: any, index: number) => {
        if (variant === 'list') {
            return (
                <div key={`${component.id}-${index}`} style={inlineStyles}>
                    {questionNode ? renderFaqChild(questionNode, item) : null}
                    {answerNodes.map((child) => (
                        <React.Fragment key={child.id}>{renderFaqChild(child, item)}</React.Fragment>
                    ))}
                </div>
            );
        }
        return (
            <details key={`${component.id}-${index}`} style={inlineStyles} open={index === 0}>
                <summary style={summaryStyle}>
                    <div className="min-w-0 flex-1">
                        {questionNode ? renderFaqChild(questionNode, item) : null}
                    </div>
                    <span style={{ marginLeft: '12px', fontSize: '20px', lineHeight: 1, color: '#64748b' }}>+</span>
                </summary>
                <div style={{ padding: '0 18px 16px 18px' }}>
                    {answerNodes.map((child) => (
                        <React.Fragment key={child.id}>{renderFaqChild(child, item)}</React.Fragment>
                    ))}
                </div>
            </details>
        );
    };

    if (repeated && repeatedItems.length > 0) {
        return (
            <div
                className="w-full"
                style={variant === 'grid' ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' } : undefined}
            >
                {repeatedItems.map((item: any, index: number) => renderItem(item, index))}
            </div>
        );
    }

    return renderItem(data, 0);
};

export default FaqItemComponent;
